



































































































































import Vue from 'vue'
import Component from 'vue-class-component'
import RespondecoForm from '@/shared/components/respondeco-form.vue'
import RespondecoStepper from '@/shared/components/respondeco-stepper.vue'
import RespondecoStep from '@/shared/components/respondeco-step.vue'
import OfferSettingsBasic from '@/business/offers/components/offer-settings-basic.vue'
import { OfferRequest, OfferResponse } from '@/api/generated'
import { EditableImageChangedEvent } from '@/shared/models/editable-image-changed-event'
import {
  CREATE_OFFER,
  FETCH_DATA_FOR_OFFER_SETTINGS,
  REMOVE_OFFER,
  UPDATE_OFFER,
} from '@/business/offers/store/types'
import { ExtendedBlob } from '@/shared/models/extended-blob'
import { loggerFor } from '@/shared/utils/logger'
import { OffersModule } from '@/business/offers/store/offers-module'
import { Injector } from '@/config/dependency-injection'
import { LoaderHelper } from '@/shared/services/loader-helper'
import { Prop } from 'vue-property-decorator'
import RespondecoTopBar from '@/main/respondeco-top-bar.vue'
import { NotificationService } from '@/shared/services/notification-service'
import { VueRouter } from 'vue-router/types/router'
import { RouterToken } from '@/config/injection-tokens'
import RespondecoLoadingIndicator from '@/shared/components/respondeco-loading-indicator.vue'
import UnifyList from '@/shared/components/unify/unify-list.vue'
import UnifyListItem from '@/shared/components/unify/unify-list-item.vue'
import UnifyListItemLabel from '@/shared/components/unify/unify-list-item-label.vue'
import UnifyListItemContent from '@/shared/components/unify/unify-list-item-content.vue'
import UnifyFormToggle from '@/shared/components/unify/unify-form-toggle.vue'
import UnifyButton from '@/shared/components/unify/unify-button.vue'
import OfferSettingsSettings from '@/business/offers/components/offer-settings-settings.vue'
import { TypedForm } from '@/shared/forms/typed-form'
import { OfferForm, offerFormMapping } from '@/business/offers/models/offer-form'
import { offerRequestResponseMapping } from '@/business/offers/models/offer-request-response-mapping'
import RespondecoTypedForm from '@/shared/components/respondeco-typed-form.vue'
import RespondecoBlock from '@/shared/components/respondeco-block.vue'
import RespondecoEditor from '@/shared/components/respondeco-editor.vue'
import RespondecoResourceGallery from '@/shared/components/respondeco-resource-gallery.vue'
import RespondecoSaveBar from '@/shared/components/respondeco-save-bar.vue'
import OfferSettingsPartners from '@/business/offers/components/offer-settings-partners.vue'
import { AuthenticationService } from '@/authentication/authentication-service'
import { environmentConfiguration } from '@/config/environments'
import { HomeService } from '@/business/home/home-service'

@Component({
  components: {
    OfferSettingsPartners,
    RespondecoSaveBar,
    RespondecoResourceGallery,
    RespondecoEditor,
    RespondecoBlock,
    RespondecoTypedForm,
    OfferSettingsSettings,
    UnifyButton,
    UnifyFormToggle,
    UnifyListItemContent,
    UnifyListItemLabel,
    UnifyListItem,
    UnifyList,
    RespondecoLoadingIndicator,
    OfferSettingsBasic,
    RespondecoStep,
    RespondecoStepper,
    RespondecoForm,
    RespondecoTopBar,
  },
})
export default class OfferSettings extends Vue {
  @Prop()
  public readonly id!: number | string

  public branding = environmentConfiguration().branding
  public form!: TypedForm<OfferResponse, OfferForm>

  private authenticationService = Injector.get(AuthenticationService)
  private homeService = Injector.get(HomeService)
  private loaderHelper = Injector.get(LoaderHelper)
  private notificationService = Injector.get(NotificationService)
  private offersModule: OffersModule = Injector.get(OffersModule)
  private router = Injector.get<VueRouter>(RouterToken)

  private logger = loggerFor('Offer Settings')
  private headerImage?: ExtendedBlob
  private image?: ExtendedBlob
  private requestResponseMapping = offerRequestResponseMapping()
  private unsaved = false

  get isNew() {
    return this.form.value.id === undefined
  }

  get loading() {
    return this.loaderHelper.isLoading(FETCH_DATA_FOR_OFFER_SETTINGS)
  }

  get notNew() {
    return !this.isNew
  }

  get offer() {
    return this.viewModel.offer
  }

  get project() {
    return this.isNew
      ? this.authenticationService.authenticatedUser().organisation.project
      : this.offer.project
  }

  get removing() {
    return this.loaderHelper.isLoading(REMOVE_OFFER)
  }

  get saving() {
    return this.loaderHelper.isLoading(UPDATE_OFFER) || this.loaderHelper.isLoading(CREATE_OFFER)
  }

  get viewModel() {
    return this.offersModule.offerSettings
  }

  public data() {
    return {
      form: new TypedForm(offerFormMapping(), this),
    }
  }

  public mounted() {
    this.offersModule
      .fetchDataForOfferSettings({
        offerId: Number(this.id),
      })
      .then(viewModel => {
        this.form.reset(viewModel.offer)
      })
  }

  public reset() {
    this.form.reset(this.offer)
  }

  public setImage(event: EditableImageChangedEvent) {
    this.image = event.image
    this.unsaved = true
  }

  public setHeaderImage(event: EditableImageChangedEvent) {
    this.headerImage = event.image
    this.unsaved = true
  }

  public submit() {
    this.logger.debug('Submit', this.offer)

    if (this.form.invalid) {
      this.$v.$touch()
      return
    }

    const model: OfferRequest = this.requestResponseMapping.deserialize(
      this.form.serialize(this.offer)
    )

    if (model.id !== undefined) {
      this.offersModule
        .updateOffer({
          offerId: model.id,
          offerRequest: model,
        })
        .then(() => {
          this.notificationService.success('common.successfulSave')

          this.homeService.konstanz$.markStale()
          this.reset()
        })
        .catch(this.notificationService.errorHandler())
    } else {
      this.offersModule
        .createOffer({
          offerRequest: model,
        })
        .then((offer: OfferResponse) => {
          this.notificationService.success('common.successfulSave')

          this.router.replace({
            name: 'offer-settings',
            params: {
              id: String(offer.id),
            },
          })

          this.homeService.konstanz$.markStale()
          this.reset()
        })
        .catch(this.notificationService.errorHandler())
    }
  }

  public validations() {
    return this.form.validations()
  }
}
